import React, { useEffect, useRef, useState } from "react";
import {
  Search,
  Bell,
  User,
  Edit,
  Plus,
  Building,
  MapPin,
  Mail,
  Briefcase,
  Link as LinkIcon,
  Phone,
  X,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import profileImage from "../../utils/profile.png";
import UserProfilePopup from "../layout/UserProfilePopup";
import AddExperienceModal from "./AddExperienceModal";
import { getFormattedDate } from "../../utils/formatDate";
import { useUpdateScout, useUploadScoutAvatar } from "../../hooks/queries/useScouts";
import { useAuth } from "../../context/AuthContext";

const POSITION_OPTIONS = [
  "Presidente",
  "Director de academia",
  "Director deportivo",
  "Head Scout",
  "Scout",
  "Coach",
  "Representante",
];

const RecruiterProfile = ({
  onNotificationsClick,
  onSearchClick,
  scoutData,
}) => {
  const { signOut } = useAuth();
  const [isProfilePopupOpen, setIsProfilePopupOpen] = useState(false);
  const [isAddExperienceModalOpen, setIsAddExperienceModalOpen] =
    useState(false);
  const [isVerificationModalOpen, setIsVerificationModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [editMessage, setEditMessage] = useState("");
  const [editForm, setEditForm] = useState({
    full_name: "",
    organization: "",
    country: "",
    email: "",
    position: "",
    website: "",
    phone: "",
    avatar_url: "",
  });

  const profileButtonRef = useRef(null);
  const photoInputRef = useRef(null);
  const updateScout = useUpdateScout();
  const uploadAvatar = useUploadScoutAvatar();
  // A picked photo previews from a local object URL until "Guardar" uploads it.
  const [pendingPhoto, setPendingPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");

  useEffect(
    () => () => {
      if (photoPreview) URL.revokeObjectURL(photoPreview);
    },
    [photoPreview],
  );

  useEffect(() => {
    if (!isEditModalOpen) {
      setPendingPhoto(null);
      setPhotoPreview("");
    }
  }, [isEditModalOpen]);

  const details = scoutData?.details || {};
  const organizationInfo = details.organization_info || {};
  const publicProfile = details.public_profile || {};
  const preferences = details.preferences || {};

  const recruiterInfo = {
    organization:
      scoutData?.organization_name || organizationInfo.name || "Sin academia",
    country: scoutData?.country || preferences.country || "--",
    email: scoutData?.email || "--",
    position: scoutData?.position || details.position || scoutData?.role || "Coach",
    website: scoutData?.website || publicProfile.website || "--",
    phone: scoutData?.phone || publicProfile.phone || "--",
  };

  const firstName = scoutData?.full_name?.split(" ")[0] || "Coach";
  const fullName = scoutData?.full_name || "Coach";
  const avatarUrl =
    publicProfile.avatar_url || details.avatar_url || scoutData?.avatar_url || profileImage;
  const experiences = [];

  useEffect(() => {
    const nextDetails = scoutData?.details || {};
    const nextOrg = nextDetails.organization_info || {};
    const nextPublic = nextDetails.public_profile || {};
    const nextPrefs = nextDetails.preferences || {};

    setEditForm({
      full_name: scoutData?.full_name || "",
      organization: scoutData?.organization_name || nextOrg.name || "",
      country: scoutData?.country || nextPrefs.country || "",
      email: scoutData?.email || "",
      position: scoutData?.position || nextDetails.position || "",
      website: scoutData?.website || nextPublic.website || "",
      phone: scoutData?.phone || nextPublic.phone || "",
      avatar_url:
        nextPublic.avatar_url || nextDetails.avatar_url || scoutData?.avatar_url || "",
    });
    setPendingPhoto(null);
    setPhotoPreview("");
  }, [scoutData]);

  const handleSaveExperience = (experienceData) => {
    console.log("Nueva experiencia guardada:", experienceData);
  };

  const handleEditChange = (field, value) => {
    setEditForm((previous) => ({ ...previous, [field]: value }));
    setEditMessage("");
  };

  const handleEditPhotoChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setEditMessage("Selecciona una imagen valida.");
      return;
    }
    setEditMessage("");
    setPendingPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleSaveProfile = async () => {
    if (!scoutData?.id) {
      setEditMessage("No se encontro el perfil del scout.");
      return;
    }

    setIsSavingProfile(true);
    setEditMessage("");

    try {
      // A newly picked photo goes to R2 first; details then carry its URL.
      const avatarUrl = pendingPhoto
        ? (await uploadAvatar.mutateAsync({ id: scoutData.id, file: pendingPhoto })).avatar_url
        : editForm.avatar_url;

      const updatedDetails = {
        ...details,
        organization_info: {
          ...organizationInfo,
          name: editForm.organization,
          type: organizationInfo.type || "academy",
        },
        public_profile: {
          ...publicProfile,
          website: editForm.website,
          phone: editForm.phone,
          avatar_url: avatarUrl,
        },
        avatar_url: avatarUrl,
        preferences: {
          ...preferences,
          country: editForm.country,
        },
        position: editForm.position,
      };

      // details es jsonb y el PATCH reemplaza el blob completo — ya viene
      // mergeado arriba. El email no es editable vía /scouts/:id. La mutación
      // invalida la sesión, así que el perfil nuevo llega con el refetch.
      await updateScout.mutateAsync({
        id: scoutData.id,
        full_name: editForm.full_name,
        phone: editForm.phone,
        organization_name: editForm.organization,
        details: updatedDetails,
      });

      setEditMessage("Perfil actualizado.");
      setTimeout(() => setIsEditModalOpen(false), 600);
    } catch (error) {
      setEditMessage(error?.message || "No se pudo guardar el perfil.");
    } finally {
      setIsSavingProfile(false);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50">
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <p className="text-gray-600">{getFormattedDate()}</p>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
              Bienvenido de vuelta, {firstName}
            </h1>
          </div>
          <div className="hidden lg:flex items-center gap-4 relative">
            <button
              type="button"
              onClick={onSearchClick}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Buscar atletas"
            >
              <Search size={20} className="text-gray-700" />
            </button>
            <button
              onClick={onNotificationsClick}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Bell size={20} className="text-gray-700" />
            </button>
            <button
              ref={profileButtonRef}
              onClick={() => setIsProfilePopupOpen(!isProfilePopupOpen)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <User size={20} className="text-gray-700" />
            </button>

            <UserProfilePopup
              isOpen={isProfilePopupOpen}
              onClose={() => setIsProfilePopupOpen(false)}
              buttonRef={profileButtonRef}
              userData={scoutData}
              onLogout={signOut}
              onViewProfile={() => setIsProfilePopupOpen(false)}
            />
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-xl p-5 sm:p-6 shadow-sm">
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6">
              <div className="w-24 h-24 rounded-full overflow-hidden flex-shrink-0 border-4 border-white shadow-lg">
                <img
                  src={avatarUrl}
                  alt={fullName}
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="flex-1 flex flex-col sm:flex-row items-center sm:items-start justify-between gap-3 text-center sm:text-left">
                <div>
                  <h2 className="text-2xl font-bold text-blue-900 mb-1">
                    {fullName}
                  </h2>
                  <p className="text-base text-gray-700 font-normal">
                    {recruiterInfo.position}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setIsVerificationModalOpen(true)}
                    className="bg-blue-900 text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition-colors flex items-center gap-2 font-medium"
                  >
                    <CheckCircle2 size={18} />
                    Verificar Perfil
                  </button>
                  <button
                    onClick={() => setIsEditModalOpen(true)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <Edit size={20} className="text-gray-700" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-5 sm:p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-blue-900">
                Toda mi informacion
              </h3>
              <button
                onClick={() => setIsEditModalOpen(true)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Edit size={18} className="text-gray-700" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <InfoItem
                  icon={Building}
                  label="Academia/Club afiliado"
                  value={recruiterInfo.organization}
                />
                <InfoItem
                  icon={MapPin}
                  label="Pais de Residencia"
                  value={recruiterInfo.country}
                />
                <InfoItem
                  icon={Mail}
                  label="Correo electronico"
                  value={recruiterInfo.email}
                />
              </div>

              <div className="space-y-4">
                <InfoItem
                  icon={Briefcase}
                  label="Cargo"
                  value={recruiterInfo.position}
                />
                <InfoItem
                  icon={LinkIcon}
                  label="Sitio Web"
                  value={recruiterInfo.website}
                />
                <InfoItem
                  icon={Phone}
                  label="Telefono de Contacto"
                  value={recruiterInfo.phone}
                />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-5 sm:p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-blue-900">Experiencia</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsAddExperienceModalOpen(true)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Plus size={18} className="text-gray-700" />
                </button>
                <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                  <Edit size={18} className="text-gray-700" />
                </button>
              </div>
            </div>

            {experiences.length > 0 ? (
              <div className="space-y-6">
                {experiences.map((exp) => (
                  <div
                    key={exp.id}
                    className="flex items-start gap-4 pb-6 border-b border-gray-200 last:border-b-0 last:pb-0"
                  >
                    <div className="w-3 h-3 bg-blue-900 rounded mt-2 flex-shrink-0"></div>
                    <div className="flex-1">
                      <h4 className="text-base font-bold text-blue-900 mb-1">
                        {exp.title}
                      </h4>
                      <p className="text-sm text-gray-700 mb-1">
                        {exp.company}
                      </p>
                      <p className="text-xs text-gray-500 mb-1">{exp.period}</p>
                      <p className="text-xs text-gray-500 mb-2">
                        {exp.location}
                      </p>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {exp.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-10 text-center border-2 border-dashed border-gray-200 rounded-lg">
                <p className="text-gray-400 text-sm">
                  Aun no hay experiencias registradas.
                </p>
                <button
                  onClick={() => setIsAddExperienceModalOpen(true)}
                  className="mt-3 text-sm text-blue-600 hover:underline"
                >
                  Agregar primera experiencia
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <AddExperienceModal
        isOpen={isAddExperienceModalOpen}
        onClose={() => setIsAddExperienceModalOpen(false)}
        onSave={handleSaveExperience}
      />

      {isEditModalOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setIsEditModalOpen(false)}
          ></div>
          <div className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-t-2xl sm:rounded-xl bg-white p-5 sm:p-6 shadow-2xl">
            <button
              type="button"
              onClick={() => setIsEditModalOpen(false)}
              className="absolute right-4 top-4 rounded-lg p-2 transition-colors hover:bg-gray-100"
            >
              <X size={20} className="text-gray-700" />
            </button>

            <div className="mb-6">
              <h3 className="text-2xl font-bold text-blue-900">
                Editar perfil
              </h3>
              <p className="mt-1 text-sm text-gray-600">
                Actualiza tu informacion basica y foto de perfil.
              </p>
            </div>

            <div className="mb-6 flex items-center gap-4">
              <div className="relative h-24 w-24 flex-shrink-0">
                <div className="h-full w-full overflow-hidden rounded-full border-4 border-white shadow-lg">
                  <img
                    src={photoPreview || editForm.avatar_url || profileImage}
                    alt={editForm.full_name || "Scout"}
                    className="h-full w-full object-cover"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => photoInputRef.current?.click()}
                  aria-label="Cambiar foto"
                  title="Cambiar foto"
                  className="absolute right-0 top-0 flex h-8 w-8 translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-blue-900 text-white shadow-lg ring-2 ring-white transition-colors hover:bg-blue-800"
                >
                  <Edit size={16} />
                </button>
              </div>
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                onChange={handleEditPhotoChange}
                className="hidden"
              />
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {[
                ["full_name", "Nombre completo", "text"],
                ["country", "Pais de residencia", "text"],
                ["email", "Correo electronico", "email", true],
                ["website", "Sitio web", "url"],
                ["phone", "Telefono de contacto", "tel"],
              ].map(([field, label, type, readOnly]) => (
                <label key={field} className="space-y-2">
                  <span className="text-sm font-semibold text-blue-900">
                    {label}
                  </span>
                  <input
                    type={type}
                    value={editForm[field]}
                    onChange={readOnly ? undefined : (event) =>
                      handleEditChange(field, event.target.value)
                    }
                    readOnly={!!readOnly}
                    className={`w-full rounded-lg border px-3 py-2 ${
                      readOnly
                        ? "border-gray-200 bg-gray-50 text-gray-500 cursor-not-allowed"
                        : "border-gray-300 text-gray-900 focus:border-transparent focus:ring-2 focus:ring-blue-600"
                    }`}
                  />
                </label>
              ))}
              <label className="space-y-2">
                <span className="text-sm font-semibold text-blue-900">
                  Academia/Club afiliado
                </span>
                <input
                  type="text"
                  value={editForm.organization}
                  onChange={(event) =>
                    handleEditChange("organization", event.target.value)
                  }
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 focus:border-transparent focus:ring-2 focus:ring-blue-600"
                />
              </label>
              <label className="space-y-2">
                <span className="text-sm font-semibold text-blue-900">
                  Cargo
                </span>
                <select
                  value={editForm.position}
                  onChange={(event) =>
                    handleEditChange("position", event.target.value)
                  }
                  className={`w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-600 ${
                    editForm.position ? "text-gray-900" : "text-gray-400"
                  }`}
                >
                  <option value="">Selecciona un cargo</option>
                  {POSITION_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {editMessage && (
              <p className="mt-5 text-center text-sm font-medium text-gray-700">
                {editMessage}
              </p>
            )}

            <div className="mt-6 flex justify-end gap-3 border-t border-gray-200 pt-5">
              <button
                type="button"
                onClick={() => setIsEditModalOpen(false)}
                className="rounded-lg bg-gray-100 px-5 py-2 font-semibold text-gray-700 transition-colors hover:bg-gray-200"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSaveProfile}
                disabled={isSavingProfile}
                className="flex items-center gap-2 rounded-lg bg-blue-900 px-6 py-2 font-semibold text-white transition-colors hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSavingProfile ? (
                  <>
                    Guardando
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </>
                ) : (
                  "Guardar cambios"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {isVerificationModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
            onClick={() => setIsVerificationModalOpen(false)}
          ></div>

          <div className="relative bg-white rounded-xl p-5 sm:p-8 max-h-[90vh] w-full max-w-md overflow-y-auto shadow-2xl">
            <button
              onClick={() => setIsVerificationModalOpen(false)}
              className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X size={20} className="text-gray-700" />
            </button>

            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-blue-900 mb-2">
                Verificacion KYC
              </h3>
              <p className="text-sm text-gray-600">
                Completa tu verificacion de identidad escaneando el codigo QR
                con tu dispositivo movil
              </p>
            </div>

            <div className="flex justify-center mb-6">
              <div className="bg-white p-4 rounded-lg border-2 border-gray-200">
                <QRCodeSVG
                  value="https://verify.didit.me/session/Uq27juIi0VXd"
                  size={200}
                  level="H"
                  includeMargin={true}
                />
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-700 text-center">
                <strong className="text-blue-900">Proceso KYC:</strong> Escanea
                el codigo QR con la camara de tu celular para continuar con la
                verificacion de identidad. Este proceso es rapido y seguro.
              </p>
            </div>

            <button
              onClick={() => setIsVerificationModalOpen(false)}
              className="w-full bg-blue-900 text-white font-bold py-3 px-6 rounded-lg hover:bg-blue-800 transition-colors"
            >
              Aceptar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const InfoItem = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <Icon size={20} className="text-blue-900 mt-1 flex-shrink-0" />
    <div className="flex-1">
      <p className="text-sm font-medium text-blue-900 mb-1">{label}</p>
      <p className="text-sm text-gray-600">{value || "--"}</p>
    </div>
  </div>
);

export default RecruiterProfile;
